import { useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";
import { PageHero } from "@/components/site/Bits";
import { Reveal } from "@/components/site/Reveal";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ nom: "", prenom: "", entreprise: "", email: "", telephone: "", sujet: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/contact", form);
      toast.success(t("contact.success"));
      setForm({ nom: "", prenom: "", entreprise: "", email: "", telephone: "", sujet: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus-ring";

  return (
    <div>
      <PageHero eyebrow={t("contact.eyebrow")} title={t("contact.title")} subtitle={t("contact.subtitle")} />

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-24 grid lg:grid-cols-5 gap-10">
        <Reveal className="lg:col-span-2">
          <div className="glass rounded-3xl p-8 h-full">
            <h3 className="font-display font-bold text-xl text-white">BBR.GO</h3>
            <p className="text-cyan-400 text-sm mt-1">{t("contact.infoTagline")}</p>
            <div className="mt-8 space-y-4">
              <a href="mailto:contactbbrgo@gmail.com" className="flex items-center gap-3 text-zinc-300 hover:text-cyan-400 transition-colors duration-300"><Mail className="w-5 h-5" /> contactbbrgo@gmail.com</a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-3">
          <form onSubmit={submit} className="glass rounded-3xl p-8 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder={t("contact.prenom")} value={form.prenom} onChange={set("prenom")} className={inputCls} data-testid="contact-prenom" />
              <input required placeholder={t("contact.nom")} value={form.nom} onChange={set("nom")} className={inputCls} data-testid="contact-nom" />
            </div>
            <input placeholder={t("contact.entreprise")} value={form.entreprise} onChange={set("entreprise")} className={inputCls} data-testid="contact-entreprise" />
            <div className="grid sm:grid-cols-2 gap-4">
              <input required type="email" placeholder={t("contact.email")} value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email" />
              <input placeholder={t("contact.telephone")} value={form.telephone} onChange={set("telephone")} className={inputCls} data-testid="contact-telephone" />
            </div>
            <input required placeholder={t("contact.sujet")} value={form.sujet} onChange={set("sujet")} className={inputCls} data-testid="contact-sujet" />
            <textarea required placeholder={t("contact.message")} rows={5} value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message" />
            <button type="submit" disabled={loading} data-testid="contact-submit"
              className="w-full bg-brand-gradient text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:opacity-90 transition-opacity duration-300 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("contact.submit")}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
